'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, GitBranch, Zap, Shield, CheckCircle, Code, BarChart3, Cpu, Workflow, Settings, Rocket, Monitor } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const devOpsData = {
  title: "DevOps",
  subtitle: "Streamline development and operations with automated CI/CD pipelines, infrastructure as code, and continuous monitoring solutions",
  headerGradient: "bg-gradient-to-r from-green-600 via-teal-500 to-blue-500",
  accentColor: "text-green-600",
  buttonColor: "bg-green-600 hover:bg-green-700",
  
  overview: "DevOps combines development and operations teams to improve collaboration, automate processes, and accelerate software delivery. Our DevOps solutions help organizations achieve faster deployment cycles, improved reliability, enhanced security, and better scalability through automation and continuous integration/continuous deployment (CI/CD) practices.",
  
  capabilities: [
    {
      name: "Continuous Integration/Continuous Deployment (CI/CD)",
      description: "Automated pipelines for building, testing, and deploying applications",
      icon: GitBranch,
      applications: ["Automated Testing", "Code Quality Checks", "Deployment Automation", "Release Management"],
      useCases: ["Faster releases", "Reduced errors", "Consistent deployments", "Rollback capabilities"]
    },
    {
      name: "Infrastructure as Code (IaC)",
      description: "Manage and provision infrastructure through code and automation",
      icon: Code,
      applications: ["Terraform", "CloudFormation", "Ansible", "Kubernetes"],
      useCases: ["Consistent environments", "Version control", "Scalable infrastructure", "Disaster recovery"]
    },
    {
      name: "Monitoring & Observability",
      description: "Real-time monitoring, logging, and performance tracking",
      icon: Monitor,
      applications: ["Application Monitoring", "Log Management", "Performance Metrics", "Alerting Systems"],
      useCases: ["Proactive issue detection", "Performance optimization", "Troubleshooting", "Capacity planning"]
    }
  ],
  
  technologies: [
    {
      category: "CI/CD Tools",
      tools: ["Jenkins", "GitLab CI", "GitHub Actions", "Azure DevOps", "CircleCI", "Travis CI"],
      description: "Continuous integration and deployment platforms"
    },
    {
      category: "Infrastructure as Code",
      tools: ["Terraform", "AWS CloudFormation", "Ansible", "Pulumi", "Chef", "Puppet"],
      description: "Infrastructure automation and configuration management"
    },
    {
      category: "Containerization",
      tools: ["Docker", "Kubernetes", "OpenShift", "Helm", "Istio", "Rancher"],
      description: "Container orchestration and management platforms"
    },
    {
      category: "Monitoring & Logging",
      tools: ["Prometheus", "Grafana", "ELK Stack", "Datadog", "New Relic", "Splunk"],
      description: "Application and infrastructure monitoring solutions"
    },
    {
      category: "Version Control",
      tools: ["Git", "GitHub", "GitLab", "Bitbucket", "Azure Repos", "SVN"],
      description: "Source code management and collaboration platforms"
    },
    {
      category: "Cloud Platforms",
      tools: ["AWS", "Azure", "Google Cloud", "DigitalOcean", "Linode", "Heroku"],
      description: "Cloud infrastructure and platform services"
    }
  ],
  
  industries: [
    {
      name: "Financial Technology",
      applications: ["Payment processing", "Trading platforms", "Risk management", "Compliance automation"],
      impact: "75% faster deployment cycles, 99.9% uptime for critical systems"
    },
    {
      name: "E-commerce",
      applications: ["Online stores", "Inventory systems", "Payment gateways", "Customer analytics"],
      impact: "50% reduction in deployment time, 90% fewer production issues"
    },
    {
      name: "Healthcare",
      applications: ["Patient management", "Telemedicine", "Medical records", "Compliance systems"],
      impact: "HIPAA-compliant deployments, 60% faster feature delivery"
    },
    {
      name: "Media & Entertainment",
      applications: ["Content delivery", "Streaming platforms", "Digital assets", "Live broadcasting"],
      impact: "Zero-downtime deployments, 40% cost reduction in infrastructure"
    },
    {
      name: "SaaS Companies",
      applications: ["Multi-tenant platforms", "API services", "User management", "Analytics dashboards"],
      impact: "10x faster scaling, 80% reduction in manual operations"
    },
    {
      name: "Gaming",
      applications: ["Game servers", "Player analytics", "In-game purchases", "Live updates"],
      impact: "Real-time deployments, 99.99% availability during peak traffic"
    }
  ],
  
  devOpsPractices: [
    {
      practice: "Continuous Integration",
      description: "Automatically integrate code changes and run tests",
      benefits: ["Early bug detection", "Faster feedback", "Improved code quality", "Reduced integration issues"],
      tools: ["Jenkins", "GitLab CI", "GitHub Actions", "Azure Pipelines"]
    },
    {
      practice: "Continuous Deployment",
      description: "Automatically deploy tested code to production",
      benefits: ["Faster time to market", "Reduced manual errors", "Consistent deployments", "Quick rollbacks"],
      tools: ["Spinnaker", "ArgoCD", "Flux", "AWS CodeDeploy"]
    },
    {
      practice: "Infrastructure as Code",
      description: "Manage infrastructure through version-controlled code",
      benefits: ["Reproducible environments", "Version control", "Automated provisioning", "Disaster recovery"],
      tools: ["Terraform", "CloudFormation", "Pulumi", "Ansible"]
    },
    {
      practice: "Monitoring & Alerting",
      description: "Continuous monitoring of applications and infrastructure",
      benefits: ["Proactive issue detection", "Performance insights", "Capacity planning", "SLA compliance"],
      tools: ["Prometheus", "Grafana", "Datadog", "New Relic"]
    }
  ],
  
  process: [
    {
      step: "1",
      title: "Assessment & Strategy",
      description: "Evaluate current development and deployment processes",
      details: ["Current state analysis", "Bottleneck identification", "Tool evaluation", "DevOps roadmap creation"]
    },
    {
      step: "2", 
      title: "CI/CD Pipeline Setup",
      description: "Implement automated build, test, and deployment pipelines",
      details: ["Pipeline design", "Automated testing", "Quality gates", "Deployment automation"]
    },
    {
      step: "3",
      title: "Infrastructure Automation",
      description: "Implement Infrastructure as Code and container orchestration",
      details: ["IaC implementation", "Container strategy", "Environment provisioning", "Security integration"]
    },
    {
      step: "4",
      title: "Monitoring & Optimization",
      description: "Set up comprehensive monitoring and continuous improvement",
      details: ["Monitoring setup", "Alerting configuration", "Performance optimization", "Process refinement"]
    }
  ],
  
  benefits: [
    "Reduce deployment time by up to 90%",
    "Increase deployment frequency by 200x", 
    "Improve system reliability and uptime",
    "Enhance security through automated compliance",
    "Enable faster feature delivery and innovation",
    "Reduce manual errors and operational overhead",
    "Improve collaboration between teams",
    "Scale infrastructure automatically based on demand"
  ],
  
  challenges: [
    {
      challenge: "Cultural Resistance",
      solution: "Gradual adoption with training and change management",
      description: "Help teams embrace DevOps culture and practices"
    },
    {
      challenge: "Legacy System Integration",
      solution: "Phased modernization and hybrid approaches",
      description: "Integrate DevOps practices with existing legacy systems"
    },
    {
      challenge: "Security & Compliance",
      solution: "DevSecOps practices and automated security testing",
      description: "Embed security throughout the development lifecycle"
    },
    {
      challenge: "Tool Complexity",
      solution: "Standardized toolchains and comprehensive training",
      description: "Simplify tool selection and provide proper training"
    }
  ]
};

export default function DevOpsPage() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <header className={`py-20 md:py-28 text-white ${devOpsData.headerGradient} relative overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
                             radial-gradient(circle at 75% 75%, white 2px, transparent 2px),
                             radial-gradient(circle at 50% 50%, white 1px, transparent 1px)`,
            backgroundSize: '50px 50px, 60px 60px, 30px 30px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link href={`/${currentLocale}`} className="inline-flex items-center text-sm text-white/80 hover:text-white transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              {localize("Back to Homepage")}
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
                {localize(devOpsData.title)}
              </h1>
              <p className="text-xl text-green-100 mb-8 leading-relaxed">
                {localize(devOpsData.subtitle)}
              </p>
              <div className="flex flex-wrap gap-3">
                {["CI/CD", "Docker", "Kubernetes", "Terraform"].map((tag, index) => (
                  <Badge key={tag} variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                    {localize(tag)}
                  </Badge>
                ))}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <Rocket className="w-12 h-12 mx-auto mb-3 text-green-200" />
                    <div className="text-2xl font-bold">90%</div>
                    <div className="text-sm text-green-100">{localize("Faster Deployments")}</div>
                  </div>
                  <div className="text-center">
                    <Zap className="w-12 h-12 mx-auto mb-3 text-green-200" />
                    <div className="text-2xl font-bold">200x</div>
                    <div className="text-sm text-green-100">{localize("More Frequent Releases")}</div>
                  </div>
                  <div className="text-center">
                    <Shield className="w-12 h-12 mx-auto mb-3 text-green-200" />
                    <div className="text-2xl font-bold">99.9%</div>
                    <div className="text-sm text-green-100">{localize("System Reliability")}</div>
                  </div>
                  <div className="text-center">
                    <Settings className="w-12 h-12 mx-auto mb-3 text-green-200" />
                    <div className="text-2xl font-bold">Auto</div>
                    <div className="text-sm text-green-100">{localize("Infrastructure")}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-6xl px-4 py-16">
        
        {/* Overview Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className={`text-3xl font-bold mb-6 ${devOpsData.accentColor}`}>
              {localize("What is DevOps?")}
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              {localize(devOpsData.overview)}
            </p>
          </div>
        </motion.section>

        {/* Core Capabilities */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Core DevOps Capabilities")}
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {devOpsData.capabilities.map((capability, index) => (
              <motion.div key={capability.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-green-500">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <capability.icon className="w-6 h-6 text-green-600" />
                      </div>
                      <CardTitle className="text-xl">{localize(capability.name)}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {localize(capability.description)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {capability.applications.map((app) => (
                            <Badge key={app} variant="outline" className="text-xs">
                              {localize(app)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Use Cases")}
                        </h4>
                        <ul className="space-y-1">
                          {capability.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* DevOps Practices Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("DevOps Practices & Methodologies")}
          </motion.h2>
          
          <div className="space-y-8">
            {devOpsData.devOpsPractices.map((practice, index) => (
              <motion.div key={practice.practice} variants={itemVariants}>
                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      {localize(practice.practice)}
                    </CardTitle>
                    <CardDescription className="text-base">{localize(practice.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Key Benefits")}
                        </h4>
                        <ul className="space-y-2">
                          {practice.benefits.map((benefit) => (
                            <li key={benefit} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(benefit)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Popular Tools")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {practice.tools.map((tool) => (
                            <Badge key={tool} variant="secondary" className="text-xs">
                              {tool}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Technology Stack */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("DevOps Technology Stack")}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devOpsData.technologies.map((tech, index) => (
              <Card key={tech.category} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-green-600" />
                    {localize(tech.category)}
                  </CardTitle>
                  <CardDescription>{localize(tech.description)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tech.tools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* DevOps Challenges */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("DevOps Challenges & Solutions")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {devOpsData.challenges.map((item, index) => (
              <motion.div key={item.challenge} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-red-600">{localize(item.challenge)}</CardTitle>
                    <CardDescription className="text-base">{localize(item.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-2">{localize("Our Solution")}</h4>
                      <p className="text-sm text-green-700">{localize(item.solution)}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Industry Applications */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Industry Applications")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devOpsData.industries.map((industry, index) => (
              <motion.div key={industry.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(industry.name)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <ul className="space-y-1">
                          {industry.applications.map((app) => (
                            <li key={app} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(app)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Impact")}
                        </h4>
                        <p className="text-sm text-green-700 font-medium">{localize(industry.impact)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Our Process */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Our DevOps Implementation Process")}
          </h2>
          
          <div className="space-y-8">
            {devOpsData.process.map((step, index) => (
              <div key={step.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-xl">{localize(step.title)}</CardTitle>
                      <CardDescription className="text-base">{localize(step.description)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {step.details.map((detail) => (
                          <li key={detail} className="text-sm text-slate-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            {localize(detail)}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8 md:p-12 border border-green-200">
            <h2 className="text-3xl font-bold text-center mb-8">
              {localize("Why Choose DevOps?")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {devOpsData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700">{localize(benefit)}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className="text-3xl font-bold mb-4">
              {localize("Ready to Accelerate Your Development with DevOps?")}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              {localize("Let our DevOps experts help you streamline your development processes, automate deployments, and build reliable, scalable systems.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={`/${currentLocale}/contact`}
                className={`${devOpsData.buttonColor} text-white font-semibold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
              >
                {localize("Start Your DevOps Journey")}
              </Link>
              <Link 
                href={`/${currentLocale}/services`}
                className="bg-slate-100 text-slate-700 font-semibold py-3 px-8 rounded-lg text-lg hover:bg-slate-200 transition-colors duration-300"
              >
                {localize("Explore Our Services")}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
} 